import {
    AssociatedDirectInput,
    ProductCalculationResult,
    ProductCalculationResults,
} from "../types/PersologOnlineTypes.ts";

export function makeAssociatedDirectInput(
    id: number,
    product: string,
    dominantRange: number[],
    initiativeRange: number[],
    steadyRange: number[],
    cautiousRange: number[],
): AssociatedDirectInput {
    return {
        id: id,
        input: {
            product: product,
            dimensions: [
                {
                    id: "in120-INTEGRATED",
                    name: "INTEGRATED",
                    competencies: [
                        {
                            id: "in120-INTEGRATED-D",
                            name: "D",
                            values: dominantRange,
                            min: 0,
                            max: 100,
                        },
                        {
                            id: "in120-INTEGRATED-I",
                            name: "I",
                            values: initiativeRange,
                            min: 0,
                            max: 100,
                        },
                        {
                            id: "in120-INTEGRATED-S",
                            name: "S",
                            values: steadyRange,
                            min: 0,
                            max: 100,
                        },
                        {
                            id: "in120-INTEGRATED-G",
                            name: "G",
                            values: cautiousRange,
                            min: 0,
                            max: 100,
                        },
                    ],
                    sumConstraint: "MAX",
                    max: 100
                },
            ],
        },
    };
}

export function buildProductCalculationResults(id: number, patterns: number[]): ProductCalculationResults {
    const results: ProductCalculationResult[] = patterns.map((pattern) => buildIN120CalculationResult(pattern));
    return {
        id: id,
        calculations: results,
    };
}

function buildIN120CalculationResult(pattern: number): ProductCalculationResult {
    return {
        sum: pattern,
        level: 0,
        product: "in120",
        dimensionCalculationResults: [],
    };
}

export function getPatternsForDiagramm(d: number[], i: number[], s: number[], g: number[]): number[] {
    const combinations = generateCombinations([d, i, s, g]);
    const patterns = combinations.map(determinePattern).map(toPattern);
    return patterns;
}

type DISCCompetencyValues = {
    d: number;
    i: number;
    s: number;
    g: number;
};

const LOW_THRESHOLD = 25;
const LOW_MID_VALUE = 26;
const MID_THRESHOLD = 50;
const MID_HIGH_VALUE = 51;
const HIGH_THRESHOLD = 75;
const HIGH_VALUE = 76;

/**
 * Generates possible combinations of values from a list of pairs of integers and maps them to
 * DISCCompetencyValues.
 *
 * This function takes a list of pairs where each pair represents two possible values for a competency.
 * It generates a list of DISCCompetencyValues, where each instance represents a possible combination of values.
 * Each value in a DISCCompetencyValues instance is chosen from the corresponding pair in the input list.
 * Some values are then included in the output list based on the following rules:
 * - If the difference between the min and max values in a pair is greater than 1, additional values are included
 * between the min and max values.
 * - The additional values included are 25, 26, 50, 51, 75, and 76 if they fall between the min and max values.
 * - The output list contains all possible combinations of values from the expanded lists.
 *
 * @param lists A list of pairs, where each pair contains two integer values representing the possible values of a
 * competency.
 * @return A list of DISCCompetencyValues, where each instance represents a possible combination of values.
 *
 * Example:
 * Input: [(50, 70), (60, 80), (20, 50), (10, 40)]
 * Output: [
 *     // Examples including additional values like 25, 26, 50, 51, 75, 76 where applicable
 *     DISCCompetencyValues(d=50.0, i=60.0, s=20.0, g=10.0),
 *     DISCCompetencyValues(d=50.0, i=60.0, s=25.0, g=10.0),
 *     DISCCompetencyValues(d=50.0, i=60.0, s=26.0, g=10.0),
 *     // Skipping to examples where 50, 51 would be included
 *     DISCCompetencyValues(d=50.0, i=60.0, s=50.0, g=10.0),
 *     DISCCompetencyValues(d=51.0, i=60.0, s=20.0, g=10.0),
 *     // Examples where 75, 76 could be included, but they do not fall within the given ranges
 *     // Therefore, no such entries would be directly generated based on the provided input ranges
 *     ...
 *     DISCCompetencyValues(d=70.0, i=80.0, s=50.0, g=40.0)
 * ]
 */
function generateCombinations(lists: number[][]): DISCCompetencyValues[] {
    if (lists.length === 0) return [];

    // Modified combinations function to use expanded lists
    function combinations(lists: number[][]): number[][] {
        if (lists.length === 0) return [[]];
        const first = expandList(lists[0]); // Expand the first list
        const rest = lists.slice(1);
        const restCombinations = combinations(rest); // Recursively generate combinations for the rest
        const result: number[][] = [];
        for (const item of first) {
            for (const combination of restCombinations) {
                result.push([item, ...combination]);
            }
        }
        return result;
    }

    // Generate combinations using expanded lists and map to DISCCompetencyValues
    return combinations(lists).map(([d, i, s, g]) => ({ d, i, s, g }));
}

// Function to expand each list entry to include 25, 26, 50, 51, 75 and 76 if they fall between min and max
function expandList(entry: number[]): number[] {
    const [min, max] = entry;

    const additionalValues = [
        LOW_THRESHOLD,
        LOW_MID_VALUE,
        MID_THRESHOLD,
        MID_HIGH_VALUE,
        HIGH_THRESHOLD,
        HIGH_VALUE,
    ].filter((value) => value > min && value < max);
    return [min, ...additionalValues, max];
}

/**
 * /**
 * Determines the DISC pattern based on the values of the competencies.
 *
 * This function takes a DISCCompetencyValues instance, maps its values to their corresponding competency types,
 * and determines a pattern based on the given rules. The pattern is derived from the competencies that have values
 * greater than 50, sorted in descending order of their values. If values are equal, they are sorted according to
 * the order of "DISC".
 *
 * Rules:
 * - If no competency value is >50, the pattern is "C".
 * - If one competency value is >50, the pattern is the single competency.
 * - If two competency values are >50:
 * - If the values are equal, the pattern is sorted by the order of "DISC".
 * - Otherwise, the pattern is the two competencies in descending order of their values.
 * - If three competency values are >50, the pattern is the three competencies sorted by the order of "DISC".
 * - If all four competency values are >50, the pattern is "DIS".
 *
 * Example:
 * Input: DISCCompetencyValues(d=70.0, i=60.0, s=20.0, g=10.0)
 * Output: "DI"
 *
 * @param values A DISCCompetencyValues instance containing the values for D, I, S, and C competencies.
 * @returns A string representing the determined DISC pattern.
 */
function determinePattern(values: DISCCompetencyValues): string {
    const competencyValues: { [key: string]: number } = {
        D: values.d,
        I: values.i,
        S: values.s,
        C: values.g,
    };

    const highCompetencies = Object.keys(competencyValues)
        .filter((key) => competencyValues[key] > 50)
        .sort((a, b) => competencyValues[b] - competencyValues[a]);

    switch (highCompetencies.length) {
        case 0:
            return "C";
        case 1:
            return highCompetencies.join("");
        case 2: {
            if (competencyValues[highCompetencies[0]] === competencyValues[highCompetencies[1]]) {
                const sortedHighCompetencies = [...highCompetencies];
                sortedHighCompetencies.sort((a, b) => "DISC".indexOf(a) - "DISC".indexOf(b));
                return sortedHighCompetencies.join("");
            } else {
                return highCompetencies.join("");
            }
        }
        case 3: {
            const sortedCompetencies = [...highCompetencies];
            sortedCompetencies.sort((a, b) => "DISC".indexOf(a) - "DISC".indexOf(b));
            return sortedCompetencies.join("");
        }
        case 4:
            return "DIS";
        default:
            return "";
    }
}

/**
 * Converts a DISC pattern string to a number.
 * @param pattern A DISC pattern string.
 * @returns A number representing the DISC pattern.
 */
function toPattern(pattern: string): number {
    return Number.parseInt(
        pattern
            .split("")
            .map((char) => {
                switch (char) {
                    case "D":
                        return "1";
                    case "I":
                        return "2";
                    case "S":
                        return "3";
                    case "C":
                        return "4";
                    default:
                        throw new Error(`Invalid pattern character: ${char}`);
                }
            })
            .join(""),
    );
}
